import { useCallback, useEffect, useState } from 'react'
import {
    Box, Button, Card, CardContent, CardHeader, Chip, FormControl,
    Grid, IconButton, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Tooltip, Typography
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import SaveIcon from '@mui/icons-material/Save'
import CachedIcon from '@mui/icons-material/Cached'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import toast from 'react-hot-toast'
import { AsyncSelect } from 'src/components/forms'
import { useForm } from 'react-hook-form'
import {
    HttpObjetosSeguridadABM,
    PerfilPermisoViewDTO,
    PerfilPermisoPorObjetoViewDTO,
    PerfilPermisoUpdateDTO,
    PerfilPermisoPerfilUpdateDTO
} from '../../objetos-seguridad/api/httpObjetosSeguridadABM'
import { HttpPerfilesABM } from '../../perfiles/api/httpPerfilesABM'

function ConfigPermisos() {
    const [tab, setTab] = useState(0)

    return (
        <Card>
            <CardHeader title='Configuración de Permisos' />
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2 }}>
                <Tab label='Por Perfil' />
                <Tab label='Por Objeto de Seguridad' />
            </Tabs>
            <CardContent>
                {tab === 0 && <PermisosPorPerfil />}
                {tab === 1 && <PermisosPorObjeto />}
            </CardContent>
        </Card>
    )
}

// ============================================================
// TAB 1: Permisos por Perfil
// ============================================================
function PermisosPorPerfil() {
    const { control, watch } = useForm<{ intIdPerfil: number }>()
    const selectedPerfil = watch('intIdPerfil')
    const [permisos, setPermisos] = useState<PerfilPermisoViewDTO[]>([])
    const [editRows, setEditRows] = useState<PerfilPermisoUpdateDTO[]>([])
    const [loading, setLoading] = useState(false)
    const [permisoTipos, setPermisoTipos] = useState<{ id: number; descripcion: string }[]>([])
    const [objetos, setObjetos] = useState<{ id: number; descripcion: string }[]>([])

    useEffect(() => {
        HttpObjetosSeguridadABM.getPermisoTipos().then(setPermisoTipos)
        HttpObjetosSeguridadABM.getObjetos().then((data: any[]) =>
            setObjetos(data.filter(o => o.bitActivo).map(o => ({ id: o.id, descripcion: o.descripcion })))
        )
    }, [])

    const cargar = useCallback(() => {
        if (!selectedPerfil) return
        setLoading(true)
        HttpObjetosSeguridadABM.getPermisosPorPerfil(selectedPerfil)
            .then((data: PerfilPermisoViewDTO[]) => {
                setPermisos(data)
                setEditRows(data.map(p => ({
                    intIdObjetoSeguridad: p.intIdObjetoSeguridad,
                    tinCodPerfilPermisoTipo: p.tinCodPerfilPermisoTipo
                })))
            })
            .catch(() => toast.error('Error al cargar permisos'))
            .finally(() => setLoading(false))
    }, [selectedPerfil])

    useEffect(() => { cargar() }, [cargar])

    const handleChangePermiso = (idObjeto: number, tipo: number) => {
        setEditRows(prev => prev.map(r =>
            r.intIdObjetoSeguridad === idObjeto ? { ...r, tinCodPerfilPermisoTipo: tipo } : r
        ))
    }

    const handleRemove = (idObjeto: number) => {
        setEditRows(prev => prev.filter(r => r.intIdObjetoSeguridad !== idObjeto))
    }

    const handleAdd = (idObjeto: number) => {
        if (editRows.some(r => r.intIdObjetoSeguridad === idObjeto)) return
        setEditRows(prev => [...prev, { intIdObjetoSeguridad: idObjeto, tinCodPerfilPermisoTipo: 2 }])
    }

    const handleSave = () => {
        if (!selectedPerfil) return
        const promise = HttpObjetosSeguridadABM.actualizarPermisosPorPerfil(selectedPerfil, editRows)
        toast.promise(promise, {
            loading: 'Guardando...', success: 'Permisos actualizados', error: 'Error'
        }).then(cargar)
    }

    const getDescObjeto = (idObjeto: number) =>
        objetos.find(o => o.id === idObjeto)?.descripcion ??
        permisos.find(p => p.intIdObjetoSeguridad === idObjeto)?.descObjetoSeguridad ?? ''

    const objetosDisponibles = objetos.filter(o => !editRows.some(r => r.intIdObjetoSeguridad === o.id))

    const columnas: GridColDef[] = [
        {
            headerName: 'Objeto', field: 'intIdObjetoSeguridad', flex: 2,
            renderCell: ({ value }) => getDescObjeto(value)
        },
        {
            headerName: 'Permiso', field: 'tinCodPerfilPermisoTipo', flex: 1,
            renderCell: ({ row }) => (
                <FormControl size='small' fullWidth>
                    <Select
                        value={row.tinCodPerfilPermisoTipo}
                        onChange={e => handleChangePermiso(row.intIdObjetoSeguridad, Number(e.target.value))}
                    >
                        {permisoTipos.map(pt => (
                            <MenuItem key={pt.id} value={pt.id}>{pt.descripcion}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        },
        {
            field: 'actions', headerName: '', width: 50,
            renderCell: ({ row }) => (
                <IconButton size='small' onClick={() => handleRemove(row.intIdObjetoSeguridad)}>
                    <DeleteIcon fontSize='small' color='error' />
                </IconButton>
            )
        }
    ]

    return (
        <Stack spacing={2}>
            <Grid container spacing={2} alignItems='center'>
                <Grid item xs={4}>
                    <AsyncSelect
                        control={control}
                        name='intIdPerfil'
                        label='Perfil'
                        loadOptions={() => HttpPerfilesABM.getPerfiles(
                            { actualPage: 1, pageSize: 999 },
                            { soloActivos: true }
                        ).then(res => res.lista)}
                        fullWidth
                    />
                </Grid>
                {selectedPerfil && (
                    <>
                        <Grid item>
                            <FormControl size='small' sx={{ minWidth: 200 }}>
                                <InputLabel>Agregar objeto</InputLabel>
                                <Select
                                    label='Agregar objeto'
                                    value=''
                                    onChange={e => handleAdd(Number(e.target.value))}
                                >
                                    {objetosDisponibles.map(o => (
                                        <MenuItem key={o.id} value={o.id}>{o.descripcion}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <Button variant='contained' startIcon={<SaveIcon />} onClick={handleSave}>
                                Guardar
                            </Button>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={cargar}><CachedIcon /></IconButton>
                        </Grid>
                    </>
                )}
            </Grid>
            {selectedPerfil && (
                <DataGrid
                    rows={editRows}
                    columns={columnas}
                    getRowId={row => row.intIdObjetoSeguridad}
                    loading={loading}
                    autoHeight
                    density='compact'
                    disableColumnMenu
                    hideFooter={editRows.length <= 25}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
            )}
        </Stack>
    )
}

// ============================================================
// TAB 2: Permisos por Objeto
// ============================================================
function PermisosPorObjeto() {
    const { control, watch } = useForm<{ intIdObjetoSeguridad: number }>()
    const selectedObjeto = watch('intIdObjetoSeguridad')
    const [permisos, setPermisos] = useState<PerfilPermisoPorObjetoViewDTO[]>([])
    const [editRows, setEditRows] = useState<PerfilPermisoPerfilUpdateDTO[]>([])
    const [loading, setLoading] = useState(false)
    const [permisoTipos, setPermisoTipos] = useState<{ id: number; descripcion: string }[]>([])
    const [perfiles, setPerfiles] = useState<{ id: number; descripcion: string }[]>([])

    useEffect(() => {
        HttpObjetosSeguridadABM.getPermisoTipos().then(setPermisoTipos)
        HttpPerfilesABM.getPerfiles(
            { actualPage: 1, pageSize: 999 },
            { soloActivos: true }
        ).then(res => setPerfiles(res.lista))
    }, [])

    const cargar = useCallback(() => {
        if (!selectedObjeto) return
        setLoading(true)
        HttpObjetosSeguridadABM.getPermisosPorObjeto(selectedObjeto)
            .then((data: PerfilPermisoPorObjetoViewDTO[]) => {
                setPermisos(data)
                setEditRows(data.map(p => ({
                    intIdPerfil: p.intIdPerfil,
                    tinCodPerfilPermisoTipo: p.tinCodPerfilPermisoTipo
                })))
            })
            .catch(() => toast.error('Error al cargar permisos'))
            .finally(() => setLoading(false))
    }, [selectedObjeto])

    useEffect(() => { cargar() }, [cargar])

    const handleChangePermiso = (idPerfil: number, tipo: number) => {
        setEditRows(prev => prev.map(r =>
            r.intIdPerfil === idPerfil ? { ...r, tinCodPerfilPermisoTipo: tipo } : r
        ))
    }

    const handleRemove = (idPerfil: number) => {
        setEditRows(prev => prev.filter(r => r.intIdPerfil !== idPerfil))
    }

    const handleAdd = (idPerfil: number) => {
        if (editRows.some(r => r.intIdPerfil === idPerfil)) return
        setEditRows(prev => [...prev, { intIdPerfil: idPerfil, tinCodPerfilPermisoTipo: 2 }])
    }

    const handleSave = () => {
        if (!selectedObjeto) return
        const promise = HttpObjetosSeguridadABM.actualizarPermisosPorObjeto(selectedObjeto, editRows)
        toast.promise(promise, {
            loading: 'Guardando...', success: 'Permisos actualizados', error: 'Error'
        }).then(cargar)
    }

    const getDescPerfil = (idPerfil: number) =>
        perfiles.find(p => p.id === idPerfil)?.descripcion ??
        permisos.find(p => p.intIdPerfil === idPerfil)?.nombrePerfil ?? ''

    const perfilesDisponibles = perfiles.filter(p => !editRows.some(r => r.intIdPerfil === p.id))

    const columnas: GridColDef[] = [
        {
            headerName: 'Perfil', field: 'intIdPerfil', flex: 2,
            renderCell: ({ value }) => getDescPerfil(value)
        },
        {
            headerName: 'Permiso', field: 'tinCodPerfilPermisoTipo', flex: 1,
            renderCell: ({ row }) => (
                <FormControl size='small' fullWidth>
                    <Select
                        value={row.tinCodPerfilPermisoTipo}
                        onChange={e => handleChangePermiso(row.intIdPerfil, Number(e.target.value))}
                    >
                        {permisoTipos.map(pt => (
                            <MenuItem key={pt.id} value={pt.id}>{pt.descripcion}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        },
        {
            field: 'actions', headerName: '', width: 50,
            renderCell: ({ row }) => (
                <IconButton size='small' onClick={() => handleRemove(row.intIdPerfil)}>
                    <DeleteIcon fontSize='small' color='error' />
                </IconButton>
            )
        }
    ]

    return (
        <Stack spacing={2}>
            <Grid container spacing={2} alignItems='center'>
                <Grid item xs={4}>
                    <AsyncSelect
                        control={control}
                        name='intIdObjetoSeguridad'
                        label='Objeto de Seguridad'
                        loadOptions={() => HttpObjetosSeguridadABM.getObjetos().then((data: any[]) =>
                            data.filter(o => o.bitActivo).map(o => ({ id: o.id, descripcion: o.descripcion }))
                        )}
                        fullWidth
                    />
                </Grid>
                {selectedObjeto && (
                    <>
                        <Grid item>
                            <FormControl size='small' sx={{ minWidth: 200 }}>
                                <InputLabel>Agregar perfil</InputLabel>
                                <Select
                                    label='Agregar perfil'
                                    value=''
                                    onChange={e => handleAdd(Number(e.target.value))}
                                >
                                    {perfilesDisponibles.map(p => (
                                        <MenuItem key={p.id} value={p.id}>{p.descripcion}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item>
                            <Button variant='contained' startIcon={<SaveIcon />} onClick={handleSave}>
                                Guardar
                            </Button>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={cargar}><CachedIcon /></IconButton>
                        </Grid>
                    </>
                )}
            </Grid>
            {selectedObjeto && (
                <DataGrid
                    rows={editRows}
                    columns={columnas}
                    getRowId={row => row.intIdPerfil}
                    loading={loading}
                    autoHeight
                    density='compact'
                    disableColumnMenu
                    hideFooter={editRows.length <= 25}
                    initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                />
            )}
        </Stack>
    )
}

export default ConfigPermisos
